import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { chatApi, scriptApi, fileApi, Session, FileUpload, Script, ScriptBreakdown, ChatMode } from '../services/api';
import { executeMultiPhaseWorkflow, WorkflowOrchestrator, WorkflowCallbacks } from '../services/workflowOrchestrator';
import { WorkflowSidebarHandle } from './WorkflowSidebar';
import CollapsibleCode from './CollapsibleCode';
import TitleEditor from './TitleEditor';
import ModeSelector from './ModeSelector';
import CitationRenderer from './CitationRenderer';
import FileDisplay from './FileDisplay';
import ThinkingBlock from './ThinkingBlock';
import StreamingMarkdown from './StreamingMarkdown';
import StableStreamingMessage from './StableStreamingMessage';
import DebugStreamingMessage from './DebugStreamingMessage';
import { useSession } from '../contexts/SessionContext';
import { getUserFriendlyErrorMessage } from '../utils/errorUtils';
import ValidationWarning from './ValidationWarning';
import FixPreview from './FixPreview';
import { ValidationIssue, Fix, ValidationResult } from '../types/validation';
import { MetadataEditor } from './MetadataEditor';
import { columnMappingApi } from '../services/columnMappingApi';
import { MetadataReviewData } from '../types/columnMapping';
import TaskConfirmation from './TaskConfirmation';
import TaskIdentificationCard from './TaskIdentificationCard';
import ResumeWorkflowCard from './ResumeWorkflowCard';
import InterruptModal from './InterruptModal';
import { useWorkflowInterrupt } from '../hooks/useWorkflowInterrupt';
import DataHealthChatMessage from './DataHealthChatMessage';
import SaveDataHandlingModal from './SaveDataHandlingModal';
import FileRelationshipModal from './FileRelationshipModal';
import QAChatMessage from './QAChatMessage';
import './Chat.css';

interface ChatProps {
  session: Session;
  uploadedFiles: FileUpload[];
  currentScript?: Script | null;
  onScriptGenerated: (script: Script, breakdown?: ScriptBreakdown) => void;
  onMessageSent?: () => void;
  onTitleUpdate?: (newTitle: string) => void;
  initialMessage?: string | null;
  onInitialMessageSent?: () => void;
  onFilesChanged?: (files: FileUpload[]) => void;
  workflowSidebarRef?: React.RefObject<WorkflowSidebarHandle>;
  onWorkflowContextChange?: (contextId: string | null) => void;
  onPhaseChange?: (phase: number) => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  fileContext?: string[]; // File names that were attached to this message
}

const Chat: React.FC<ChatProps> = ({ session, uploadedFiles, currentScript, onScriptGenerated, onMessageSent, onTitleUpdate, initialMessage, onInitialMessageSent, onFilesChanged, workflowSidebarRef, onWorkflowContextChange, onPhaseChange }) => {
  const { updateSessionMode } = useSession();
  const [messages, setMessages] = useState<Message[]>([]);
  
  const [inputMessage, setInputMessage] = useState('');
  // Track files for non-workflow modes (chat/research)
  const [localFiles, setLocalFiles] = useState<FileUpload[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [workflowOrchestrator, setWorkflowOrchestrator] = useState<WorkflowOrchestrator | null>(null);
  const [awaitingQAResponse, setAwaitingQAResponse] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [isFixingIssues, setIsFixingIssues] = useState(false);
  const [pendingScriptId, setPendingScriptId] = useState<string | null>(null);
  const [showFixPreview, setShowFixPreview] = useState(false);
  const [showMetadataEditor, setShowMetadataEditor] = useState(false);
  const [metadataReviewData, setMetadataReviewData] = useState<MetadataReviewData | null>(null);
  const [workflowContextId, setWorkflowContextId] = useState<string | null>(null);
  const [hasColumnMappings, setHasColumnMappings] = useState(false);
  const metadataResolveRef = useRef<((value: boolean) => void) | null>(null);
  const [showTaskConfirmation, setShowTaskConfirmation] = useState(false);
  const [taskIdentification, setTaskIdentification] = useState<any>(null);
  const taskConfirmResolveRef = useRef<((confirmed: boolean, selectedTask?: string) => void) | null>(null);
  const [proposedFixes, setProposedFixes] = useState<Fix[]>([]);
  const [interruptedWorkflow, setInterruptedWorkflow] = useState<any>(null);
  const [currentPhase, setCurrentPhase] = useState<number>(1);
  const [showDataHealthCheck, setShowDataHealthCheck] = useState(false);
  const [dataHealthData, setDataHealthData] = useState<any>(null);
  const dataHealthResolveRef = useRef<((choices: Record<string, string> | null) => void) | null>(null);
  const [showSaveDataHandlingModal, setShowSaveDataHandlingModal] = useState(false);
  const [dataHandlingChoices, setDataHandlingChoices] = useState<Record<string, any> | null>(null);
  const [taskType, setTaskType] = useState<string>('general');
  const [showFileRelationshipReview, setShowFileRelationshipReview] = useState(false);
  const [fileRelationshipData, setFileRelationshipData] = useState<any>(null);
  const fileRelationshipResolveRef = useRef<((relationships: any | null) => void) | null>(null);
  const [qaReviewData, setQaReviewData] = useState<any>(null); // Updated to hold qaStatus, qaData, and qaAnalysisText
  const [showQaReviewCard, setShowQaReviewCard] = useState(false);
  
  // Loading states for better UX
  const [isCheckingDataHealth, setIsCheckingDataHealth] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [isProcessingPhase, setIsProcessingPhase] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Streaming optimization refs
  const lastRenderTimeRef = useRef<number>(0);
  const streamingMessageIndexRef = useRef<number | null>(null);
  
  // Get the current mode from the session
  const currentMode: ChatMode = session.mode || 'chat';
  
  // Workflow interrupt handling
  const {
    isInterrupting,
    canResume,
    showInterruptModal,
    escPressCount,
    handleInterrupt,
    cancelInterrupt,
    resumeWorkflow
  } = useWorkflowInterrupt({
    enabled: currentMode === 'workflow' && loading,
    onInterrupt: async (reason) => {
      console.log('[Chat] Workflow interrupted:', reason);
      
      if (workflowContextId) {
        try {
          const response = await fetch(`/api/workflow/interrupt`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              context_id: workflowContextId,
              reason
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            // Store interrupted workflow info
            setInterruptedWorkflow({
              contextId: workflowContextId,
              interruptedAt: new Date().toISOString(),
              reason,
              currentPhase: currentPhase || 1,
              canResume: data.canResume !== false,
              resumeToken: data.resumeToken
            });
          }
        } catch (error) {
          console.error('[Chat] Failed to interrupt workflow:', error);
        }
      }
      setLoading(false);
    }
  });

  // Cleanup refs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup any pending data health resolver
      if (dataHealthResolveRef.current) {
        console.log('[Chat] Cleaning up pending data health resolver');
        dataHealthResolveRef.current(null);
        dataHealthResolveRef.current = null;
      }
      
      // Cleanup any pending task confirmation resolver
      if (taskConfirmResolveRef.current) {
        console.log('[Chat] Cleaning up pending task confirmation resolver');
        taskConfirmResolveRef.current(false);
        taskConfirmResolveRef.current = null;
      }
      
      // Cancel any pending orchestrator operations
      if (workflowOrchestrator) {
        console.log('[Chat] Canceling pending orchestrator operations');
        // The orchestrator should handle its own cleanup
      }
    };
  }, []); // Empty deps = only on unmount
  
  // Sync uploaded files from props to localFiles for non-workflow modes
  useEffect(() => {
    if (currentMode !== 'workflow' && uploadedFiles.length > 0) {
      setLocalFiles(uploadedFiles);
    }
  }, [uploadedFiles, currentMode]);

  const loadChatHistory = useCallback(async () => {
    try {
      const response = await chatApi.getHistory(session.session_id);
      // Only set messages if we don't have any local messages yet
      // This prevents overwriting messages that are being sent
      setMessages(prev => {
        if (prev.length === 0) {
          return response.data.messages;
        }
        // If we already have messages, don't overwrite them
        return prev;
      });
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  }, [session.session_id]);

  // Restore workflow context ID from session if available
  useEffect(() => {
    if (session.workflow_context_id && !workflowContextId) {
      console.log('[Chat] Restored workflow context from session:', session.workflow_context_id);
      setWorkflowContextId(session.workflow_context_id);
      if (onWorkflowContextChange) {
        onWorkflowContextChange(session.workflow_context_id);
      }
      
      // Check if QA has been completed and we should recreate the orchestrator
      const lastMessages = messages.slice(-10);
      const qaCompleted = lastMessages.some(msg => 
        msg.content.includes('QA APPROVED') ||
        msg.content.includes('NEEDS REVISION') ||
        msg.content.includes('✅ Script has been revised successfully') ||
        msg.content.includes('The script has been revised') ||
        msg.content.includes('proceed with execution') ||
        msg.content.includes('Quality Assurance Review')
      );
      
      // If QA is complete and we have a script, recreate the orchestrator for Phase 4
      if (qaCompleted && currentScript?.script_id && currentMode === 'workflow' && !workflowOrchestrator) {
        console.log('[Chat] QA completed, recreating orchestrator for Phase 4 continuations');
        
        // Create a minimal orchestrator that can handle Phase 4
        const orchestrator = new WorkflowOrchestrator({
          onPhaseStart: (phase) => {
            console.log(`[Workflow] Phase ${phase} started`);
            setIsProcessingPhase(true);  // Set processing state
            if (phase === 4 && workflowSidebarRef?.current) {
              workflowSidebarRef.current.setPhaseExecuting(true);
              workflowSidebarRef.current.startScriptStreaming();
            }
          },
          onPhaseComplete: (phase, result) => {
            console.log(`[Workflow] Phase ${phase} completed`, result);
            if (phase === 4 && workflowSidebarRef?.current) {
              workflowSidebarRef.current.setPhaseExecuting(false);
            }
          },
          onPhaseError: (phase, error) => console.error(`[Workflow] Phase ${phase} error:`, error),
          onChunk: (phase, chunk) => {
            if (phase === 4 && workflowSidebarRef?.current) {
              workflowSidebarRef.current.appendScriptChunk(chunk);
            }
          },
          onScriptGenerated: (script, breakdown) => {
            console.log('[Workflow] Script generated in Phase 4:', script);
            onScriptGenerated(script, breakdown);
            if (workflowSidebarRef?.current) {
              workflowSidebarRef.current.endScriptStreaming(script);
            }
          },
        });
        
        // Set the context and script IDs directly (bypass phase execution)
        // Using bracket notation to access private properties
        (orchestrator as any).contextId = session.workflow_context_id;
        (orchestrator as any).scriptId = currentScript.script_id;
        
        // Mark phases 1-3 as completed since QA is done
        (orchestrator as any).phases = [
          { phase: 1, status: 'completed' },
          { phase: 2, status: 'completed' },
          { phase: 3, status: 'completed' },
          { phase: 4, status: 'pending' }
        ];
        
        setWorkflowOrchestrator(orchestrator);
      }
      
      // Check if the context has column mappings saved
      console.log('[Chat] Checking workflow context for column data:', session.workflow_context_id);
      fetch(`/api/workflow/context/${session.workflow_context_id}/metadata-review`)
        .then(response => {
          console.log('[Chat] Metadata review response status:', response.status);
          if (response.ok) {
            return response.json();
          }
          if (response.status === 404) {
            throw new Error('Workflow context not found - it may have expired');
          }
          throw new Error(`Failed to fetch workflow context: ${response.status}`);
        })
        .then(data => {
          console.log('[Chat] Metadata review data received:', {
            hasReviewData: !!data.reviewData,
            totalColumns: data.reviewData?.total_columns,
            mappingsCount: data.reviewData?.mappings?.length,
            hasBeenModified: data.hasBeenModified
          });
          
          // Check if either user-confirmed mappings OR initial categorization exists
          // reviewData.mappings will be populated from either source by the backend
          if (data.reviewData && data.reviewData.mappings && data.reviewData.mappings.length > 0) {
            setHasColumnMappings(true);
            console.log('[Chat] ✓ Workflow context has column data:', data.reviewData.mappings.length, 'columns ready for script generation');
          } else if (data.reviewData && data.reviewData.total_columns > 0) {
            // Even if mappings array is empty, if we have total_columns, Phase 1 ran
            setHasColumnMappings(true);
            console.log('[Chat] ✓ Workflow context has column categorization from initial analysis');
          } else {
            setHasColumnMappings(false);
            console.log('[Chat] ✗ Workflow context has no column data yet - Phase 1 may not have completed');
          }
        })
        .catch(error => {
          console.error('[Chat] Error checking workflow context:', error.message || error);
          setHasColumnMappings(false);
        });
    }
  }, [session.workflow_context_id, workflowContextId, onWorkflowContextChange, messages, currentScript, currentMode, workflowOrchestrator, onScriptGenerated]);

  const handleModeChange = async (newMode: ChatMode) => {
    try {
      await updateSessionMode(session.session_id, newMode);
    } catch (error) {
      console.error('Failed to update session mode:', error);
    }
  };
  
  const handleFixWithAI = async () => {
    if (!pendingScriptId || validationIssues.length === 0) return;
    
    setIsFixingIssues(true);
    try {
      // Get the original request from the last user message
      const originalRequest = messages
        .filter(m => m.role === 'user')
        .slice(-1)[0]?.content || '';
      
      // Call fix-issues endpoint
      const response = await scriptApi.fixIssues(pendingScriptId, {
        issues: validationIssues,
        original_request: originalRequest
      });
      
      let fixMessage = '';
      let fixes: any[] = [];
      
      // Process streaming response
      for await (const chunk of response) {
        const data = JSON.parse(chunk);
        
        if (data.type === 'chunk') {
          fixMessage += data.content;
          // Update UI with streaming fix explanation
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === 'assistant' && lastMessage.content.includes('🔧 Analyzing issues')) {
              lastMessage.content = `🔧 Analyzing issues...\n\n${fixMessage}`;
            } else {
              newMessages.push({
                role: 'assistant',
                content: `🔧 Analyzing issues...\n\n${fixMessage}`,
                timestamp: new Date().toISOString()
              });
            }
            return newMessages;
          });
        } else if (data.type === 'complete') {
          fixes = data.fixes || [];
          
          if (fixes.length > 0) {
            // Show fix preview instead of automatically applying
            setProposedFixes(fixes);
            setShowFixPreview(true);
            setShowValidationWarning(false);
            
            // Add completion message
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.role === 'assistant') {
                lastMessage.content += '\n\n✅ Fixes generated. Please review and apply them using the preview above.';
              }
              return newMessages;
            });
          } else {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: '⚠️ No fixes could be generated. Please edit the script manually.',
              timestamp: new Date().toISOString()
            }]);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fix issues:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Failed to generate fixes. Please try editing manually.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsFixingIssues(false);
    }
  };
  
  // Check for data handling differences after successful Phase 2
  const checkForDataHandlingDifferences = async (contextId: string) => {
    try {
      setIsCheckingDataHealth(true);
      
      const response = await fetch(`/api/workflow/context/${contextId}/check-data-handling-differences`);
      
      if (!response.ok) {
        console.error('[DataHandling] Failed to check differences:', response.status);
        // Don't silently fail - show the modal anyway so user can decide
        setShowSaveDataHandlingModal(true);
        return;
      }
      
      const data = await response.json();
      
      if (data.error) {
        console.error('[DataHandling] Error checking differences:', data.error);
        // Show modal anyway on error
        setShowSaveDataHandlingModal(true);
        return;
      }
      
      // Only show modal if we should prompt (always true after Phase 2)
      if (data.should_prompt) {
        console.log('[DataHandling] Prompting to save preferences, differences:', data.has_differences);
        setShowSaveDataHandlingModal(true);
      }
      
    } catch (error) {
      console.error('[DataHandling] Failed to check differences:', error);
      // Show modal anyway on error - better to give user the option
      setShowSaveDataHandlingModal(true);
    } finally {
      setIsCheckingDataHealth(false);
    }
  };

  const handleQaAction = async (action: 'accept' | 'revise' | 'discuss') => {
    console.log('[QA Review] User selected action:', action);
    
    // Convert QA review to a static message in chat history
    if (qaReviewData) {
      const qaMessage: Message = {
        role: 'assistant',
        content: `QA Review - Action: ${action}`, // Add meaningful content
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, qaMessage]);
    }
    
    // Now hide the interactive component since it's been saved to history
    setShowQaReviewCard(false);
    setAwaitingQAResponse(false);
    
    // Create a message based on the action
    let message = '';
    switch (action) {
      case 'accept':
        message = 'The script looks good. Please proceed with execution.';
        break;
      case 'revise':
        message = 'Please fix the issues identified in the QA review.';
        break;
      case 'discuss':
        message = "Let's discuss the QA findings. What are the most important issues to address?";
        break;
    }
    
    // Send the message as if the user typed it
    if (message) {
      setInputMessage(message);
      // Trigger send after a small delay to ensure state updates
      setTimeout(() => {
        const event = new KeyboardEvent('keydown', { key: 'Enter' });
        document.querySelector('.input-section textarea')?.dispatchEvent(event);
      }, 100);
    }
  };

  const handleApplyFixes = async (fixes: Fix[]) => {
    if (!pendingScriptId) return;
    
    try {
      // Apply fixes to create new script version
      const response = await scriptApi.applyFixes(pendingScriptId, {
        fixes,
        issues_fixed: validationIssues
      });
      
      if (response.data.success) {
        // Update UI with the new script
        const newScript = response.data.script;
        
        // Add success message to chat
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✅ Successfully applied ${response.data.applied_fixes} fixes. The script has been updated.`,
          timestamp: new Date().toISOString()
        }]);
        
        // Update parent component with new script
        if (onScriptGenerated) {
          onScriptGenerated(newScript, newScript.breakdown || currentScript?.breakdown);
        }
        
        // Clear validation state
        setShowValidationWarning(false);
        setValidationIssues([]);
        setPendingScriptId(null);
      }
    } catch (error) {
      console.error('Failed to apply fixes:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Failed to apply fixes. Please try editing manually.',
        timestamp: new Date().toISOString()
      }]);
    }
  };
  
  const handleContinueAnyway = () => {
    setShowValidationWarning(false);
    setValidationIssues([]);
    setPendingScriptId(null);
  };

  useEffect(() => {
    // Load chat history when component mounts
    loadChatHistory();
    
    // Check for interrupted workflows for this session
    const checkForInterruptedWorkflows = async () => {
      // Only check if we're in workflow mode, not loading, and don't have an active workflow
      if (currentMode === 'workflow' && !loading && !workflowContextId && !interruptedWorkflow) {
        try {
          console.log('[Chat] Checking for interrupted workflows for session:', session.session_id);
          const response = await fetch(`/api/workflow/contexts/?session_id=${session.session_id}&status=interrupted`);
          
          if (response.ok) {
            const contexts = await response.json();
            console.log('[Chat] Found interrupted workflows:', contexts.length);
            
            // Get the most recent interrupted workflow
            if (contexts.length > 0) {
              const mostRecent = contexts.sort((a: any, b: any) => 
                new Date(b.interrupted_at).getTime() - new Date(a.interrupted_at).getTime()
              )[0];
              
              console.log('[Chat] Showing resume card for interrupted workflow:', mostRecent.context_id);
              setInterruptedWorkflow({
                contextId: mostRecent.context_id,
                interruptedAt: mostRecent.interrupted_at,
                reason: mostRecent.reason || 'Session was interrupted',
                currentPhase: mostRecent.phase || 1,
                canResume: mostRecent.can_resume !== false,
                resumeToken: null // Will be generated when resuming
              });
              
              // Add a message to show the resume card in the chat
              if (messages.length === 0) {
                setMessages([{
                  role: 'assistant',
                  content: 'I found an interrupted workflow from your previous session. You can resume where you left off or start fresh.',
                  timestamp: new Date().toISOString()
                }]);
              }
            }
          }
        } catch (error) {
          console.error('[Chat] Error checking for interrupted workflows:', error);
        }
      }
    };
    
    // Check for interrupted workflows after a short delay to ensure component is ready
    const timeoutId = setTimeout(checkForInterruptedWorkflows, 500);
    
    // Check for workflow edit context
    const workflowEditContext = localStorage.getItem('workflowEditContext');
    if (workflowEditContext) {
      try {
        const context = JSON.parse(workflowEditContext);
        // Only process if it's for our session and recent (within 5 minutes)
        const contextAge = Date.now() - new Date(context.timestamp).getTime();
        if (context.sessionId === session.session_id && contextAge < 5 * 60 * 1000) {
          // Set workflow mode
          handleModeChange('workflow');
          
          // Add a system message with the workflow context
          const systemMessage: Message = {
            role: 'assistant',
            content: `I'll help you edit the workflow "${context.context.workflowName}". Here's the original workflow summary:\n\n${context.context.originalInstructions}\n\nPlease describe what changes you'd like to make to this workflow.`,
            timestamp: new Date().toISOString(),
          };
          setMessages([systemMessage]);
          
          // Store the parent workflow ID for reference
          sessionStorage.setItem('parentWorkflowId', context.context.parentWorkflowId);
          
          // Clean up the context
          localStorage.removeItem('workflowEditContext');
        }
      } catch (error) {
        console.error('Failed to parse workflow edit context:', error);
      }
    }
    
    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
    };
  }, [loadChatHistory, session.session_id, currentMode, loading, workflowContextId, interruptedWorkflow]);

  // Handle initial message from CenteredChatInput
  const [hasProcessedInitialMessage, setHasProcessedInitialMessage] = useState(false);

  useEffect(() => {
    if (initialMessage && !hasProcessedInitialMessage) {
      // Set the message in the input
      setInputMessage(initialMessage);
      setHasProcessedInitialMessage(true);
      
      // Clear the pending message
      if (onInitialMessageSent) {
        onInitialMessageSent();
      }
    }
  }, [initialMessage, hasProcessedInitialMessage, onInitialMessageSent]);

  // Moved after sendMessage definition

  useEffect(() => {
    // Scroll to bottom when messages change
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    // Only auto-scroll if user is already near the bottom
    const container = messagesEndRef.current?.parentElement;
    if (!container) return;
    
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    if (isNearBottom) {
      // Use scrollTop instead of scrollIntoView to avoid affecting entire page
      container.scrollTop = container.scrollHeight;
    }
  };

  const sendMessage = useCallback(async () => {
    if (!inputMessage.trim()) return;

    // Check if we're in workflow mode and have completed QA (Phase 3)
    if (currentMode === 'workflow' && workflowOrchestrator && currentScript) {
      // Use the conversation endpoint but make it behave like Phase 2
      const userMessage: Message = {
        role: 'user',
        content: inputMessage,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');
      setAwaitingQAResponse(false);
      
      try {
        setLoading(true);
        
        // Start script streaming in sidebar (like Phase 2)
        if (workflowSidebarRef?.current) {
          workflowSidebarRef.current.setPhaseExecuting(true);
          workflowSidebarRef.current.startScriptStreaming();
        }
        
        // Let AI determine the appropriate mode based on the message content
        // Default to 'edit' but AI can override based on understanding the request
        
        // Get QA analysis from recent messages
        const qaAnalysis = messages
          .filter(m => m.role === 'assistant')
          .reverse()
          .find(m => m.content.includes('Quality Assurance Review'))?.content || '';
        
        // Call the conversation endpoint without specifying mode - let AI decide
        const response = await chatApi.workflowConversation({
          session_id: session.session_id,
          message: inputMessage,
          script_id: currentScript.script_id,
          qa_analysis: qaAnalysis,
          context_id: workflowContextId || undefined  // Pass context to retrieve structured concerns
          // mode is optional - AI will determine from message content
        });
        
        // Process streaming response
        let assistantMessage = '';
        let streamingMessageIndex: number | null = null;
        let detectedMode: string | null = null;
        
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');
        
        const decoder = new TextDecoder();
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            if (!line.trim()) continue;
            
            try {
              const data = JSON.parse(line);
              
              if (data.type === 'start') {
                // Capture the detected mode for internal use
                detectedMode = data.mode;
                
                // Prepare the sidebar based on mode (no visual indicators in chat)
                if (data.mode === 'edit' || data.mode === 'TARGETED_EDIT') {
                  // For edits, prepare the sidebar
                  if (workflowSidebarRef?.current) {
                    workflowSidebarRef.current.setPhaseExecuting(false); // Not a full phase execution
                  }
                } else if (data.mode === 'regenerate' || data.mode === 'REGENERATE') {
                  // For regeneration, prepare for new script in sidebar
                  if (workflowSidebarRef?.current) {
                    workflowSidebarRef.current.setPhaseExecuting(true);
                    workflowSidebarRef.current.startScriptStreaming();
                  }
                }
                // No prefix text - let the AI's natural response speak for itself
              } else if (data.type === 'chunk' && data.content) {
                assistantMessage += data.content;
                
                if (streamingMessageIndex === null) {
                  const newMessage: Message = {
                    role: 'assistant',
                    content: assistantMessage,
                    timestamp: new Date().toISOString(),
                  };
                  setMessages(prev => {
                    const newMessages = [...prev, newMessage];
                    streamingMessageIndex = newMessages.length - 1;
                    return newMessages;
                  });
                } else if (streamingMessageIndex !== null) {
                  setMessages(prev => {
                    if (streamingMessageIndex === null) return prev;
                    const newMessages = [...prev];
                    newMessages[streamingMessageIndex] = {
                      ...newMessages[streamingMessageIndex],
                      content: assistantMessage,
                    };
                    return newMessages;
                  });
                }
              } else if (data.type === 'complete') {
                // Handle completion based on response type
                if (data.new_script_id && currentScript) {
                  // Fetch the complete script using the new script ID
                  try {
                    const scriptResponse = await scriptApi.get(data.new_script_id);
                    const newScript = scriptResponse.data;
                    
                    // Pass the new script to parent component
                    onScriptGenerated?.(newScript, newScript.breakdown || currentScript.breakdown);
                    
                    // Show diff if available
                    if (data.diff_data && workflowSidebarRef?.current) {
                      workflowSidebarRef.current.showDiff(data.diff_data);
                    }
                  } catch (error) {
                    console.error('Failed to fetch updated script:', error);
                    // Fallback to old behavior if fetch fails
                    const newScript: Script = {
                      ...currentScript,
                      script_id: data.new_script_id,
                      code: data.edits ? data.edits[0]?.replace : currentScript.code
                    };
                    onScriptGenerated?.(newScript, currentScript.breakdown);
                  }
                }
                
                // If this was a regeneration that cleared the workflow, reset
                if (data.mode === 'regenerate' && inputMessage.toLowerCase().includes('start over')) {
                  setWorkflowOrchestrator(null);
                }
                
                // Handle validation results
                if (data.validation && data.validation.has_issues) {
                  setValidationIssues(data.validation.issues);
                  setShowValidationWarning(true);
                  setPendingScriptId(data.new_script_id);
                }
              }
            } catch (e) {
              console.error('Error parsing conversation response:', e);
            }
          }
        }
      } catch (error) {
        console.error('Failed to handle workflow conversation:', error);
        const errorMessage: Message = {
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setLoading(false);
      }
      
      return;
    }

    // For workflow mode, use uploadedFiles; for other modes, use localFiles
    const relevantFiles = currentMode === 'workflow' ? uploadedFiles : localFiles;
    
    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
      fileContext: relevantFiles.length > 0 ? relevantFiles.map(f => f.filename) : undefined,
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    
    // Force scroll to bottom when user sends a message
    setTimeout(() => {
      const container = messagesEndRef.current?.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);

    try {
      // Send message with file context if available
      // For workflow mode, use uploadedFiles; for other modes, use localFiles
      const relevantFiles = currentMode === 'workflow' ? uploadedFiles : localFiles;
      const context = relevantFiles.length > 0 
        ? { file_ids: relevantFiles.map(f => f.file_id) } 
        : undefined;
      
      // Show file processing indicator if files are being sent
      let processingFiles = relevantFiles.length > 0;
      
      // For research mode, show typing indicator instead of empty message
      let assistantMessage: Message | null = null;
      if (currentMode === 'research') {
        setIsTyping(true);
      } else {
        // For other modes, create assistant message placeholder for streaming
        assistantMessage = {
          role: 'assistant',
          content: processingFiles ? '📄 Processing files...' : '',
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => {
          // Set the streaming index to where this new message will be
          streamingMessageIndexRef.current = prev.length;
          console.log('[Workflow] Creating assistant message at index:', prev.length);
          return [...prev, assistantMessage!];
        });
      }
      
      let streamedContent = '';
      let scriptSaved = false;
      let savedScript: Script | null = null;
      let capturedValidationPlan: any = null;  // Capture validation plan from stream
      let isStreamingToSidebar = false;  // Declare at the top level
      console.log('Starting stream at', new Date().toISOString());
      console.log('Script saved flag reset to:', scriptSaved);
      
      // Reset optimization state
      lastRenderTimeRef.current = 0; // Start fresh
      // Don't set the streaming index yet - wait until we actually create the assistant message
      streamingMessageIndexRef.current = null;
      
      console.log('[Stream Init] Mode:', currentMode, 'Message index:', streamingMessageIndexRef.current);
      
      // Use multi-phase workflow for workflow mode, regular streaming for others
      if (currentMode === 'workflow') {
        // Use multi-phase workflow
        console.log('[Workflow] Starting multi-phase workflow execution');
        
        // Call onMessageSent early for title generation (don't wait for workflow completion)
        // This ensures title gets generated even if workflow is interrupted
        if (onMessageSent) {
          onMessageSent();
        }
        
        let phaseMessages: string[] = [];
        let currentPhaseMessageIndex: number | null = null;
        
        console.log('[Workflow] Starting workflow with:', {
          sessionId: session.session_id,
          fileCount: context?.file_ids?.length || 0,
          fileIds: context?.file_ids
        });
        
        const orchestrator = await executeMultiPhaseWorkflow(
          session.session_id,
          inputMessage,
          context?.file_ids,
          {
            onPhaseStart: (phase) => {
              console.log(`[Workflow] Phase ${phase} started`);
              setIsProcessingPhase(true);  // Set processing state
              setCurrentPhase(phase);
              
              // Notify sidebar that phase is executing
              if (workflowSidebarRef?.current) {
                workflowSidebarRef.current.setPhaseExecuting(true);
              }
              
              // Clear streaming message for new phase
              streamedContent = '';
              
              // Create new assistant message for each phase (except Phase 2 and 4 which go to sidebar)
              if (phase !== 2 && phase !== 4) {
                // For Phase 3, add a status message since we won't stream content
                const content = phase === 3 
                  ? '📋 Performing quality review of the generated script...' 
                  : '';
                const phaseMessage: Message = {
                  role: 'assistant',
                  content,
                  timestamp: new Date().toISOString(),
                };
                setMessages(prev => {
                  currentPhaseMessageIndex = prev.length;
                  streamingMessageIndexRef.current = prev.length;
                  return [...prev, phaseMessage];
                });
              } else if (phase === 4) {
                // For Phase 4, add a brief message in chat to indicate revision is happening
                const revisionStartMessage: Message = {
                  role: 'assistant',
                  content: '🔄 Revising the script in the sidebar...',
                  timestamp: new Date().toISOString(),
                };
                setMessages(prev => [...prev, revisionStartMessage]);
              }
            },
            
            onChunk: (phase, chunk) => {
              // Phase 2 and Phase 4 chunks go to script editor in sidebar, not chat
              if (phase === 2 || phase === 4) {
                console.log(`[Workflow] Phase ${phase} chunk received (${chunk.length} chars) - sent to sidebar`);
                // Start streaming on first chunk
                if (!isStreamingToSidebar && workflowSidebarRef?.current) {
                  workflowSidebarRef.current.startScriptStreaming();
                  isStreamingToSidebar = true;
                }
                // Stream chunk to sidebar
                workflowSidebarRef?.current?.appendScriptChunk(chunk);
                return;
              }
              
              // Phase 3 chunks should be accumulated but NOT shown in chat
              // The complete event will show the interactive QA component
              if (phase === 3) {
                streamedContent += chunk;
                console.log(`[Workflow] Phase 3 chunk received (${chunk.length} chars) - accumulating for QA component`);
                return;  // Don't update messages for Phase 3
              }
              
              streamedContent += chunk;
              
              // Update the current phase message (only for Phase 1 now)
              if (currentPhaseMessageIndex !== null) {
                setMessages(prev => {
                  const newMessages = [...prev];
                  if (currentPhaseMessageIndex !== null && currentPhaseMessageIndex < newMessages.length) {
                    newMessages[currentPhaseMessageIndex] = {
                      ...newMessages[currentPhaseMessageIndex],
                      content: streamedContent
                    };
                  }
                  return newMessages;
                });
              }
            },
            
            onPhaseError: (phase, error) => {
              console.error(`[Workflow] Phase ${phase} error:`, error);
              
              // Clear executing state on error
              if (workflowSidebarRef?.current) {
                workflowSidebarRef.current.setPhaseExecuting(false);
              }
              
              const errorMessage: Message = {
                role: 'assistant',
                content: `Error in Phase ${phase}: ${error}`,
                timestamp: new Date().toISOString(),
              };
              setMessages(prev => [...prev, errorMessage]);
            },
            
            onScriptGenerated: (script, breakdown) => {
              console.log('[Workflow] Script generated:', script);
              
              // Check if this is a revision (Phase 4)
              const currentPhase = phaseMessages.length > 0 ? phaseMessages.length : 0;
              const isRevision = currentPhase === 4;
              
              // If this is Phase 4 revision and we have an original script, create diff data
              if (isRevision && savedScript && workflowSidebarRef?.current) {
                const diffData = {
                  originalCode: savedScript.code,
                  adaptedCode: script.code,
                  changes: [], // Would need to calculate actual changes
                  preservationRatio: 0.8, // Placeholder
                  visualDiff: null
                };
                workflowSidebarRef.current.showDiff(diffData);
              }
              
              scriptSaved = true;
              savedScript = script;
              onScriptGenerated(script, breakdown);
              
              // If this is from Phase 4, add a completion message
              if (isRevision) {
                const completionMessage: Message = {
                  role: 'assistant',
                  content: '✅ Script has been revised successfully! The updated script and process flow are available in the sidebar.',
                  timestamp: new Date().toISOString(),
                };
                setMessages(prev => [...prev, completionMessage]);
              }
            },
            
            onPhaseComplete: (phase, result) => {
              console.log(`[Workflow] Phase ${phase} completed`, result);
              setIsProcessingPhase(false);  // Clear processing state
              
              // Notify sidebar that phase is done
              if (workflowSidebarRef?.current) {
                workflowSidebarRef.current.setPhaseExecuting(false);
              }
              
              // End script streaming for Phase 2 and Phase 4
              if ((phase === 2 || phase === 4) && isStreamingToSidebar && workflowSidebarRef?.current) {
                // For Phase 4, use the saved script if result.script is not available
                const scriptToPass = result.script || (phase === 4 ? savedScript : null);
                workflowSidebarRef.current.endScriptStreaming(scriptToPass);
                isStreamingToSidebar = false;
                
                // For Phase 2, poll for breakdown since it's generated async
                if (phase === 2 && result.script_id) {
                  const pollBreakdown = async () => {
                    let attempts = 0;
                    const maxAttempts = 60;  // 120 seconds total
                    const pollInterval = 2000; // 2 seconds
                    
                    const checkBreakdown = async () => {
                      try {
                        const scriptResponse = await scriptApi.get(result.script_id);
                        if (scriptResponse.data.breakdown) {
                          console.log('[Workflow] Breakdown loaded after polling');
                          onScriptGenerated(scriptResponse.data, scriptResponse.data.breakdown);
                          return true;
                        }
                      } catch (error) {
                        console.error('[Workflow] Error polling for breakdown:', error);
                      }
                      return false;
                    };
                    
                    // Initial check
                    if (await checkBreakdown()) return;
                    
                    // Poll with intervals
                    const intervalId = setInterval(async () => {
                      attempts++;
                      if (attempts >= maxAttempts) {
                        clearInterval(intervalId);
                        console.warn('[Workflow] Breakdown polling timed out, trying one final check...');
                        
                        // Try one final fetch in case it just completed
                        const finalCheck = await checkBreakdown();
                        if (!finalCheck) {
                          // Set timeout state in sidebar only if still no breakdown
                          if (workflowSidebarRef?.current) {
                            workflowSidebarRef.current.setBreakdownTimeout();
                          }
                        }
                        // Don't call onScriptGenerated here - the script is already set from Phase 2
                        // Calling it again with partial data was overwriting the full script
                        return;
                      }
                      
                      if (await checkBreakdown()) {
                        clearInterval(intervalId);
                      }
                    }, pollInterval);
                  };
                  
                  // Start polling after a short delay
                  setTimeout(pollBreakdown, 1000);
                }
                
                // Check if we should prompt to save data handling preferences
                // Add a small delay to let user see the success message first
                if (dataHandlingChoices && workflowContextId) {
                  setTimeout(() => {
                    checkForDataHandlingDifferences(workflowContextId);
                  }, 1500);  // 1.5 second delay
                }
              }
              
              // Check if Phase 3 is awaiting user response
              if (phase === 3) {
                if (result.awaiting_user_decision) {
                  // Remove the "Performing quality review..." message
                  setMessages(prev => {
                    // Remove the last message if it's the Phase 3 status message
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage?.content?.includes('Performing quality review')) {
                      return prev.slice(0, -1);
                    }
                    return prev;
                  });
                  
                  // Store QA data for chat message display
                  setQaReviewData({
                    qaStatus: result.qa_status || 'error',
                    qaData: result.structured_qa,
                    qaAnalysisText: result.qa_analysis_text
                  });
                  setShowQaReviewCard(true); // This will now trigger chat message, not overlay
                  setAwaitingQAResponse(true);
                } else if (result.awaiting_user_response) {
                  // Fallback to old behavior
                  setAwaitingQAResponse(true);
                }
              }
              
              // Reset for next phase
              streamedContent = '';
              currentPhaseMessageIndex = null;
              streamingMessageIndexRef.current = null;
            },
            
            onWorkflowComplete: () => {
              console.log('[Workflow] Multi-phase workflow completed');
              // onMessageSent already called at the start of workflow
              // Don't call again to avoid incrementing message count twice
            },
            
            onTaskConfirmation: async (taskData: any) => {
              console.log('[Workflow] Task confirmation required:', taskData);
              setTaskIdentification(taskData);
              setShowTaskConfirmation(true);
              
              // Return a promise that resolves when user makes a decision
              return new Promise<{ confirmed: boolean; selectedTask?: string }>((resolve) => {
                taskConfirmResolveRef.current = (confirmed: boolean, selectedTask?: string) => {
                  resolve({ confirmed, selectedTask });
                  setShowTaskConfirmation(false);
                };
              });
            },
            
            onMetadataReview: async (reviewData: MetadataReviewData) => {
              console.log('[Workflow] Metadata review required:', reviewData);
              setMetadataReviewData(reviewData);
              
              // Store task type if available
              if (reviewData.task_identification?.task_type) {
                setTaskType(reviewData.task_identification.task_type);
              }
              
              // Store data health for later (but don't show it yet)
              if (reviewData.data_health?.has_issues) {
                console.log('[Workflow] Data health issues detected (will handle after metadata confirmation):', reviewData.data_health);
                setDataHealthData(reviewData.data_health);
              }
              
              // FIRST: Show metadata editor for column confirmation
              setShowMetadataEditor(true);
              
              // Wait for metadata confirmation
              const metadataConfirmed = await new Promise<boolean>((resolve) => {
                metadataResolveRef.current = resolve;
              });
              
              console.log('[Workflow] Metadata confirmed:', metadataConfirmed);
              
              // THEN: Handle data health check if needed (after columns are confirmed)
              if (reviewData.data_health?.has_issues && metadataConfirmed) {
                console.log('[Workflow] Now handling data health issues after metadata confirmation');
                setDataHealthData(reviewData.data_health); // Set the data health data!
                setIsCheckingDataHealth(false); // Not loading anymore, we have the data
                setShowDataHealthCheck(true);
                
                const userChoices = await new Promise<Record<string, string> | null>((resolve) => {
                  dataHealthResolveRef.current = resolve;
                });
                
                if (userChoices) {
                  console.log('[Workflow] User provided data handling choices:', userChoices);
                  setDataHandlingChoices(userChoices);
                  
                  // Send choices to backend to update context
                  if (workflowContextId) {
                    try {
                      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/workflow/context/${workflowContextId}/data-handling`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          data_handling_choices: userChoices
                        })
                      });
                      console.log('[Workflow] Data handling choices sent to backend');
                    } catch (error) {
                      console.error('[Workflow] Failed to send data handling choices:', error);
                    }
                  }
                }
                
                setShowDataHealthCheck(false);
                setIsCheckingDataHealth(false);
              }
              
              return metadataConfirmed;
            },
            
            onDataHealthReview: async (healthData: any) => {
              console.log('[Workflow] Data health review requested:', healthData);
              setDataHealthData(healthData);
              setShowDataHealthCheck(true);
              
              return new Promise<Record<string, string> | null>((resolve) => {
                dataHealthResolveRef.current = resolve;
              });
            },
            
            onSaveDataHandling: async (choices: Record<string, any>) => {
              console.log('[Workflow] Prompting to save data handling choices:', choices);
              setDataHandlingChoices(choices);
              setShowSaveDataHandlingModal(true);
              
              // This doesn't need to wait for user response
              return true;
            },
            
            onFileRelationshipReview: async (relationshipData: any) => {
              console.log('[Workflow] File relationship review requested:', relationshipData);
              setFileRelationshipData(relationshipData);
              setShowFileRelationshipReview(true);
              
              return new Promise<any | null>((resolve) => {
                fileRelationshipResolveRef.current = resolve;
              });
            },
            
            onContextCreated: (contextId: string) => {
              console.log('[Workflow] Context created:', contextId);
              setWorkflowContextId(contextId);
              if (onWorkflowContextChange) {
                onWorkflowContextChange(contextId);
              }
            },
            onPhaseChange: (phase: number) => {
              console.log('[Workflow] Phase changed:', phase);
              if (onPhaseChange) {
                onPhaseChange(phase);
              }
            },
            
            onMetadata: (category: string, data: any) => {
              console.log('[Workflow] Metadata event:', category, data);
              if (category === 'task_identification' && data) {
                setTaskIdentification(data); // Update local state
              }
            }
          }
        );
        
        // Store the orchestrator for resuming workflow later
        setWorkflowOrchestrator(orchestrator);
      } else {
        // Use regular streaming API for chat and research modes
        await chatApi.sendMessageStream(
          session.session_id,
          inputMessage,
          context,
          currentMode,
        // onChunk: Update message content as it streams
        async (chunk) => {
          // Try to parse as JSON for special events
          try {
            const data = JSON.parse(chunk);
            
            // Handle different event types
            if (data.type === 'typing' && currentMode === 'research') {
              setIsTyping(data.isTyping);
              return; // Don't process typing events as content
            } else if (data.type === 'script') {
              // Script event from Phase 4 revision
              console.log('Received script event from stream:', data);
              
              // Save the updated script
              if (data.script && data.script.content) {
                console.log('Phase 4 revised script received, saving...');
                try {
                  const scriptResponse = await scriptApi.save(
                    session.session_id,
                    data.script.content,
                    undefined, // Breakdown will come separately
                    capturedValidationPlan
                  );
                  console.log('Revised script saved:', scriptResponse.data);
                  savedScript = scriptResponse.data;
                  
                  // Update the UI with the revised script
                  onScriptGenerated(scriptResponse.data, undefined);
                  scriptSaved = true; // Mark as saved
                } catch (error) {
                  console.error('Failed to save revised script:', error);
                }
              }
              return;
            } else if (data.type === 'validation_plan') {
              // Capture validation plan for later use
              console.log('Captured validation plan from stream');
              capturedValidationPlan = data.plan;
              return;
            } else if (data.type === 'breakdown') {
              // Breakdown event from Phase 4
              console.log('Received breakdown event from stream:', data);
              
              // Update the saved script with the breakdown
              if (savedScript && data.breakdown) {
                try {
                  const updatedScript = await scriptApi.update(
                    savedScript.script_id,
                    { breakdown: data.breakdown }
                  );
                  console.log('Script updated with revised breakdown');
                  
                  // Update the UI with the new breakdown
                  onScriptGenerated(updatedScript.data, data.breakdown);
                } catch (error) {
                  console.error('Failed to update script with breakdown:', error);
                }
              }
              return;
            }
          } catch {
            // Not JSON, treat as regular content chunk
          }
          
          streamedContent += chunk;
          
          // This block is only for chat/research modes, not workflow mode
          // Remove the workflow-specific script update detection from here
          
          // For research mode, create the assistant message on first content chunk
          if (currentMode === 'research' && !assistantMessage && streamedContent) {
            setIsTyping(false); // Hide typing indicator
            assistantMessage = {
              role: 'assistant',
              content: '',
              timestamp: new Date().toISOString(),
            };
            setMessages(prev => {
              streamingMessageIndexRef.current = prev.length;
              return [...prev, assistantMessage!];
            });
          }
          
          // Don't clear the processing indicator - let the content replace it naturally
          
          // Simple throttling - update every 50ms max
          const now = Date.now();
          const timeSinceLastUpdate = now - lastRenderTimeRef.current;
          
          if (timeSinceLastUpdate < 50) {
            // Too soon, skip this update
            return;
          }
          
          // Enough time has passed, update the UI
          lastRenderTimeRef.current = now;
          console.log('[Stream Update] Updating UI with', streamedContent.length, 'chars');
            
          // Process content for display
          let displayContent = streamedContent;
            
          // Only check for separator occasionally
          if (streamedContent.includes('---BREAKDOWN---')) {
            const separatorIndex = streamedContent.indexOf('---BREAKDOWN---');
            if (separatorIndex !== -1) {
              displayContent = streamedContent.substring(0, separatorIndex).trim();
            }
          }
            
          // Update the message
          if (streamingMessageIndexRef.current !== null) {
            setMessages(prev => {
              const newMessages = [...prev];
              if (streamingMessageIndexRef.current !== null && streamingMessageIndexRef.current < newMessages.length) {
                newMessages[streamingMessageIndexRef.current] = {
                  ...newMessages[streamingMessageIndexRef.current],
                  content: displayContent
                };
              }
              return newMessages;
            });
          }
        }, // This closes the onChunk callback
        // onComplete callback
        async (data) => {
          console.log('Stream complete at', new Date().toISOString());
          console.log('Complete data:', data);
          
          // Reset typing indicator when complete
          setIsTyping(false);
          
          // Clear streaming containers
          streamingContainersRef.current = { thinkingBlocks: new Map(), codeBlock: null };
          
          // Reset optimization state
          lastRenderTimeRef.current = 0;
          streamingMessageIndexRef.current = null;
          
          // Force final update with complete content
          if (assistantMessage && streamedContent) {
            const finalDisplayContent = streamedContent.includes('---BREAKDOWN---') 
              ? streamedContent.substring(0, streamedContent.indexOf('---BREAKDOWN---')).trim()
              : streamedContent;
            
            setMessages(prev => {
              const newMessages = [...prev];
              const lastIndex = newMessages.length - 1;
              if (lastIndex >= 0 && newMessages[lastIndex].role === 'assistant') {
                newMessages[lastIndex] = {
                  ...newMessages[lastIndex],
                  content: finalDisplayContent
                };
              }
              return newMessages;
            });
          }
          
          // Extract breakdown data from the complete response
          const breakdown = data.breakdown;
          
          // Also capture validation plan from complete event if not already captured
          if (!capturedValidationPlan && data.validation_plan) {
            capturedValidationPlan = data.validation_plan;
            console.log('Captured validation plan from complete event');
          }
          
          // Extract script from the streamed content if not already saved
          if (!scriptSaved && streamedContent.includes('```python')) {
            const scriptMatch = streamedContent.match(/```python[\s\n]([\s\S]*?)```/);
            if (scriptMatch && scriptMatch[1]) {
              const extractedCode = scriptMatch[1].trim();
              console.log('Extracted script from stream:');
              console.log('Script length:', extractedCode.length, 'characters');
              console.log('First 200 chars:', extractedCode.substring(0, 200));
              console.log('Last 200 chars:', extractedCode.substring(extractedCode.length - 200));
              
              // Check for common issues
              if (extractedCode.endsWith('import')) {
                console.error('WARNING: Script ends with incomplete import statement!');
              }
              if (!extractedCode.includes('\n')) {
                console.error('WARNING: Script appears to be a single line!');
              }
              
              try {
                // Save the extracted script directly to backend
                console.log('Saving extracted script to backend...');
                const scriptResponse = await scriptApi.save(
                  session.session_id,
                  extractedCode,
                  breakdown,
                  capturedValidationPlan
                );
                console.log('Script saved:', scriptResponse.data);
                onScriptGenerated(scriptResponse.data, breakdown);
              } catch (error) {
                console.error('Script generation failed:', error);
                // Create a local script object as fallback
                const localScript = {
                  script_id: `local-${Date.now()}`,
                  session_id: session.session_id,
                  code: extractedCode,
                  language: 'python',
                  created_at: new Date().toISOString(),
                  executed: false
                };
                console.log('Using local script:', localScript);
                onScriptGenerated(localScript, breakdown);
              }
            } else {
              console.log('No script found in response or no file uploaded');
            }
          } else if (scriptSaved && savedScript && breakdown) {
            // Script was already saved during streaming, update it with breakdown
            console.log('Updating saved script with breakdown...');
            try {
              const updatedScript = await scriptApi.update(savedScript.script_id, { breakdown });
              console.log('Script updated with breakdown:', updatedScript.data);
              onScriptGenerated(updatedScript.data, breakdown);
            } catch (error) {
              console.error('Failed to update script with breakdown:', error);
              // Still pass the breakdown even if update failed
              onScriptGenerated(savedScript, breakdown);
            }
          } else if (breakdown) {
            // Even if no script was extracted, pass the breakdown if we have one
            console.log('No script found, but breakdown available:', breakdown);
            // Note: We're in chat/research mode here, not workflow mode
            // This case might not be relevant anymore with multi-phase workflow
          } else {
            console.log('No Python code block found in response');
          }
          
          // Notify parent that a message was sent
          if (onMessageSent) {
            onMessageSent();
          }
        },
        // onError
        (error) => {
          console.error('Streaming error:', error);
          setIsTyping(false); // Reset typing indicator on error
          
          // Reset optimization state on error
          lastRenderTimeRef.current = 0;
          
          // Determine error message based on error type
          let errorContent = 'Sorry, I encountered an error. Please try again.';
          if (error && error.includes && error.includes('overloaded')) {
            errorContent = 'The AI service is currently overloaded. Please wait a moment and try again.';
          }
          
          // Handle error - create error message if no assistant message exists
          if (!assistantMessage) {
            const errorMessage: Message = {
              role: 'assistant',
              content: errorContent,
              timestamp: new Date().toISOString(),
            };
            setMessages(prev => [...prev, errorMessage]);
          } else {
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                role: assistantMessage!.role,
                timestamp: assistantMessage!.timestamp,
                content: streamedContent ? streamedContent + '\n\n[Error: ' + errorContent + ']' : errorContent
              };
              return newMessages;
            });
          }
        }
      );
      } // End of else block for regular streaming
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: getUserFriendlyErrorMessage(error),
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  }, [session.session_id, uploadedFiles, localFiles, currentMode, onScriptGenerated, onMessageSent, inputMessage, workflowSidebarRef, onWorkflowContextChange, onPhaseChange, workflowContextId, currentScript, workflowOrchestrator]);

  // Auto-send the initial message after component is ready
  useEffect(() => {
    if (hasProcessedInitialMessage && inputMessage && messages.length === 0 && !loading) {
      // Send the message directly without delay
      sendMessage();
    }
  }, [hasProcessedInitialMessage, inputMessage, messages.length, loading, sendMessage]);

  // Script generation removed - scripts are now extracted from chat responses
  
  const sendQuickPrompt = (prompt: string) => {
    setInputMessage(prompt);
    // Trigger send after setting the message
    setTimeout(() => {
      const textarea = document.querySelector('.input-container textarea') as HTMLTextAreaElement;
      if (textarea) {
        const event = new KeyboardEvent('keypress', {
          key: 'Enter',
          code: 'Enter',
          keyCode: 13,
          which: 13,
          bubbles: true
        });
        textarea.dispatchEvent(event);
      }
    }, 100);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    // Files are now allowed in all modes

    const files = Array.from(e.dataTransfer.files);
    
    // Validate file types
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'image/jpeg',
      'image/jpg',
      'image/png',
      'text/csv',
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.csv', '.xls', '.xlsx'];
    
    const invalidFiles = files.filter(file => {
      const hasValidType = allowedTypes.includes(file.type) || file.type === '';
      const hasValidExtension = allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      return !hasValidType && !hasValidExtension;
    });
    
    if (invalidFiles.length > 0) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'file-upload-error';
      errorDiv.textContent = `Unsupported file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Allowed: PDF, Word, Excel, CSV, JPG, PNG`;
      document.body.appendChild(errorDiv);
      
      setTimeout(() => {
        errorDiv.remove();
      }, 5000);
      return;
    }
    
    if (files.length === 0) {
      return;
    }
    
    if (!onFilesChanged) {
      alert('File upload not configured properly');
      return;
    }
    
    setIsUploading(true);
    try {
      const uploadPromises = files.map(file => fileApi.upload(file, session.session_id));
      const results = await Promise.all(uploadPromises);
      const newUploadedFiles = results.map(r => r.data);
      
      // Handle files based on mode
      if (currentMode === 'workflow') {
        // For workflow mode, update through parent component
        // Append new files to existing files from props
        const combinedFiles = [...uploadedFiles, ...newUploadedFiles];
        onFilesChanged(combinedFiles);
      } else {
        // For chat/research modes, store files locally
        setLocalFiles(prev => [...prev, ...newUploadedFiles]);
      }
      
      // Show success animation
      const successDiv = document.createElement('div');
      successDiv.className = 'file-upload-success';
      successDiv.textContent = `${files.length} file${files.length > 1 ? 's' : ''} uploaded!`;
      document.body.appendChild(successDiv);
      
      setTimeout(() => {
        successDiv.remove();
      }, 3000);
    } catch (error: any) {
      console.error('Error uploading files:', error);
      if (error.response?.data?.detail) {
        alert(`File upload failed: ${error.response.data.detail}`);
      } else if (error.response?.data) {
        console.error('Server error data:', error.response.data);
        alert(`File upload failed: ${JSON.stringify(error.response.data).substring(0, 200)}`);
      } else {
        alert(`File upload failed: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsUploading(false);
    }
  }, [session.session_id, onFilesChanged, currentMode, uploadedFiles]);

  const handleRemoveFile = useCallback((fileId: string) => {
    setLocalFiles(prev => prev.filter(f => f.file_id !== fileId));
  }, []);

  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Validate file types
    const fileArray = Array.from(files);
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.csv', '.xls', '.xlsx'];
    
    const invalidFiles = fileArray.filter(file => {
      const hasValidType = allowedTypes.includes(file.type) || file.type === '';
      const hasValidExtension = allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      return !hasValidType && !hasValidExtension;
    });
    
    if (invalidFiles.length > 0) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'file-upload-error';
      errorDiv.textContent = `Unsupported file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Allowed: PDF, Word, Excel, CSV, JPG, PNG`;
      document.body.appendChild(errorDiv);
      
      setTimeout(() => {
        errorDiv.remove();
      }, 5000);
      return;
    }

    setIsUploading(true);
    try {
      const uploadPromises = fileArray.map(file => fileApi.upload(file, session.session_id));
      const results = await Promise.all(uploadPromises);
      const newUploadedFiles = results.map(r => r.data);
      
      // Handle files based on mode
      if (currentMode === 'workflow') {
        // For workflow mode, update through parent component
        if (onFilesChanged) {
          // Append to existing files from props, not replace
          onFilesChanged([...uploadedFiles, ...newUploadedFiles]);
        }
      } else {
        // For chat/research modes, store files locally
        setLocalFiles(prev => [...prev, ...newUploadedFiles]);
      }
      
      // Show success animation
      const successDiv = document.createElement('div');
      successDiv.className = 'file-upload-success';
      successDiv.textContent = `${fileArray.length} file${fileArray.length > 1 ? 's' : ''} uploaded!`;
      document.body.appendChild(successDiv);
      
      setTimeout(() => {
        successDiv.remove();
      }, 3000);
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setIsUploading(false);
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [session.session_id, currentMode, onFilesChanged, uploadedFiles]);

  // Track streaming state to maintain container boundaries
  const streamingContainersRef = useRef<{
    thinkingBlocks: Map<string, { content: string; isOpen: boolean }>;
    codeBlock: { content: string; isOpen: boolean } | null;
  }>({ thinkingBlocks: new Map(), codeBlock: null });

  const renderMessage = (message: Message, index: number) => {
    if (message.role === 'assistant') {
      // Check if this is a QA review message stored in history
      if ((message as any).metadata?.type === 'qa_review') {
        const metadata = (message as any).metadata;
        return (
          <QAChatMessage
            qaStatus={metadata.qaStatus}
            qaData={metadata.qaData}
            qaAnalysisText={metadata.qaAnalysisText}
            onAction={() => {}} // No action since it's already been processed
            isProcessing={false}
            isReadOnly={true} // Add this prop to indicate it's a historical view
          />
        );
      }
      
      // Check if this is a research mode message by looking for tables or citations
      const hasTable = message.content.includes('|') && message.content.split('\n').some(line => 
        line.trim().match(/^\|.*\|.*\|/)
      );
      const hasCitations = message.content.match(/\[\d+\]/) || message.content.includes('Sources:');
      
      // Use CitationRenderer for research mode content
      if (currentMode === 'research' || hasTable || hasCitations) {
        return <CitationRenderer content={message.content} />;
      }
      
      // For workflow mode, use the debug streaming component
      const isStreaming = loading && index === messages.length - 1;
      // Log render for debugging
      if (isStreaming) {
        console.log(`[Render] Message ${index} streaming`);
      }
      
      // Debug: Check if thinking tags are present
      if (message.content.includes('<thinking>')) {
        console.log(`[Render] Message ${index} contains thinking tags:`, {
          contentPreview: message.content.substring(0, 200),
          isStreaming,
          hasClosingTag: message.content.includes('</thinking>')
        });
      }
      
      return <DebugStreamingMessage content={message.content} isStreaming={isStreaming} messageIndex={index} />;
    }
    return <div>{message.content}</div>;
  };


  return (
    <div className="chat-container">
      <div className="chat-content-wrapper">
        {onTitleUpdate && (
          <TitleEditor
            title={session.title || 'New Chat'}
            sessionId={session.session_id}
            onTitleUpdate={onTitleUpdate}
          />
        )}
        <div className="messages-container">
        {messages.map((message, index) => (
          <div key={index} className={`message ${message.role}`}>
            <div className="message-header">
              <span className="message-role">
                {message.role === 'user' ? 'You' : 'Assertion'}
              </span>
            </div>
            <div className="message-bubble-container">
              <div className="message-content">
                {renderMessage(message, index)}
              </div>
              {message.fileContext && message.fileContext.length > 0 && (
                <div className="message-file-context">
                  <span className="file-context-icon">📎</span>
                  <span className="file-context-text">
                    {message.fileContext.length} file{message.fileContext.length > 1 ? 's' : ''}: {message.fileContext.join(', ')}
                  </span>
                </div>
              )}
              <span className="message-time">
                {new Date(message.timestamp).toLocaleTimeString()}
              </span>
            </div>
          </div>
        ))}
        
        {/* Data Health Check as Chat Message */}
        {showDataHealthCheck && dataHealthData && (
          <div className="message assistant">
            <div className="message-header">
              <span className="message-role">Assertion</span>
            </div>
            <div className="message-bubble-container">
              <DataHealthChatMessage
                summary={dataHealthData.summary || ''}
                actionQuestions={dataHealthData.action_questions || []}
                hasSavedPreferences={dataHealthData.has_saved_preferences || false}
                onSubmit={(choices) => {
                  console.log('[DataHealth] User submitted choices:', choices);
                  setShowDataHealthCheck(false);
                  setDataHandlingChoices(choices);
                  
                  // Resolve the promise to let workflow continue
                  if (dataHealthResolveRef.current) {
                    dataHealthResolveRef.current(choices);
                    dataHealthResolveRef.current = null;
                  }
                  
                  // Note: We can't send messages during workflow Phase 1->2 transition
                  // because currentScript doesn't exist yet. The choices are saved in
                  // workflow context and will be included in Phase 2 generation.
                }}
                onSkip={() => {
                  console.log('[DataHealth] User skipped data health review');
                  setShowDataHealthCheck(false);
                  
                  // Resolve the promise  
                  if (dataHealthResolveRef.current) {
                    dataHealthResolveRef.current(null);
                    dataHealthResolveRef.current = null;
                  }
                  
                  // Note: Same as above - can't send messages during Phase 1->2 transition
                }}
              />
            </div>
          </div>
        )}
        
        {isTyping && (
          <div className="message assistant typing-indicator">
            <div className="message-header">
              <span className="message-role">Assertion</span>
            </div>
            <div className="message-bubble-container">
              <div className="message-content">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* QA Review Message - show after Phase 3 completes */}
        {showQaReviewCard && qaReviewData && (
          <div className="message assistant">
            <div className="message-header">
              <span className="message-role">Assertion</span>
            </div>
            <div className="message-bubble-container">
              <QAChatMessage
                qaStatus={qaReviewData.qaStatus}
                qaData={qaReviewData.qaData}
                qaAnalysisText={qaReviewData.qaAnalysisText}
                onAction={handleQaAction}
                isProcessing={loading}
              />
            </div>
          </div>
        )}
        
        {/* Resume Workflow Prompt - show in chat area when workflow is interrupted */}
        {/* Show if: workflow mode, has context, has column mappings saved, no orchestrator running */}
        {currentMode === 'workflow' && workflowContextId && hasColumnMappings && !workflowOrchestrator && (() => {
          // Check if workflow was actually interrupted (not completed and not currently running)
          const lastMessages = messages.slice(-5);
          
          // Check if workflow completed
          const isCompleted = lastMessages.some(msg => 
            msg.content.includes('Successfully executed') || 
            msg.content.includes('execution completed') ||
            msg.content.includes('Script has been executed') ||
            msg.content.includes('The script ran successfully') ||
            msg.content.includes('✅ Script executed successfully')
          );
          
          // Check if QA has already been done (either passed or revised)
          const qaAlreadyDone = lastMessages.some(msg => 
            msg.content.includes('Quality Assurance Review') ||
            msg.content.includes('QA APPROVED') ||
            msg.content.includes('NEEDS REVISION') ||
            msg.content.includes('✅ Script has been revised successfully') ||
            msg.content.includes('The script has been revised') ||
            msg.content.includes('proceed with execution')
          );
          
          // Check if a phase is currently running (streaming)
          const isCurrentlyRunning = lastMessages.some(msg => 
            msg.content.includes('Analyzing your request') ||
            msg.content.includes('Generating script') ||
            msg.content.includes('🔄 Generating') ||
            msg.content.includes('Phase 2:') ||
            msg.content.includes('Phase 3:') ||
            msg.content.includes('Reviewing script quality')
          );
          
          // Only show resume if workflow is neither completed nor currently running AND QA hasn't been done
          // hasColumnMappings already confirms Phase 1 was completed
          return !isCompleted && !isCurrentlyRunning && !qaAlreadyDone;
        })() && (
          <div className="message assistant">
            <div className="message-header">
              <span className="message-role">Assertion</span>
            </div>
            <div className="message-bubble-container">
              <div className="message-content">
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#fff5f0', 
                  borderRadius: '8px', 
                  border: '1px solid #ff9045',
                  marginBottom: '12px'
                }}>
                  <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#92400e' }}>
                    I noticed your workflow was interrupted. Your column selections have been saved. 
                    {currentScript 
                      ? "The script has been generated. Would you like me to run the quality assurance review?"
                      : "Would you like me to continue generating the script?"}
                  </p>
                  <button 
                    onClick={async () => {
                      // Resume workflow using the same mechanism as normal workflow
                      // This ensures consistent behavior
                      try {
                        setLoading(true);
                        
                        // Track script streaming state
                        let isStreamingToSidebar = false;
                        let streamedContent = '';
                        let scriptSaved = false;
                        let savedScript: Script | null = null;
                        
                        // Create callbacks (same as in handleSendMessage for workflow mode)
                        const callbacks: WorkflowCallbacks = {
                          onPhaseStart: (phase) => {
                            console.log(`[Workflow Resume] Phase ${phase} started`);
                            
                            // Notify sidebar that phase is executing
                            if (workflowSidebarRef?.current) {
                              workflowSidebarRef.current.setPhaseExecuting(true);
                            }
                            
                            // Clear streaming message for new phase
                            streamedContent = '';
                          },
                          
                          onChunk: (phase, chunk) => {
                            // Phase 2 chunks go to script editor in sidebar
                            if (phase === 2) {
                              console.log(`[Workflow Resume] Phase ${phase} chunk received (${chunk.length} chars) - sent to sidebar`);
                              // Start streaming on first chunk
                              if (!isStreamingToSidebar && workflowSidebarRef?.current) {
                                workflowSidebarRef.current.startScriptStreaming();
                                isStreamingToSidebar = true;
                              }
                              // Stream chunk to sidebar
                              workflowSidebarRef?.current?.appendScriptChunk(chunk);
                              return;
                            }
                            
                            // Other phases would update chat messages
                            streamedContent += chunk;
                          },
                          
                          onPhaseError: (phase, error) => {
                            console.error(`[Workflow Resume] Phase ${phase} error:`, error);
                            
                            // Clear executing state on error
                            if (workflowSidebarRef?.current) {
                              workflowSidebarRef.current.setPhaseExecuting(false);
                            }
                            
                            const errorMessage: Message = {
                              role: 'assistant',
                              content: `Error in Phase ${phase}: ${error}`,
                              timestamp: new Date().toISOString(),
                            };
                            setMessages(prev => [...prev, errorMessage]);
                          },
                          
                          onScriptGenerated: (script, breakdown) => {
                            console.log('[Workflow Resume] Script generated:', script);
                            scriptSaved = true;
                            savedScript = script;
                            onScriptGenerated(script, breakdown);
                          },
                          
                          onPhaseComplete: (phase, result) => {
                            console.log(`[Workflow Resume] Phase ${phase} completed`, result);
                            
                            // Notify sidebar that phase is done
                            if (workflowSidebarRef?.current) {
                              workflowSidebarRef.current.setPhaseExecuting(false);
                            }
                            
                            // End script streaming for Phase 2
                            if (phase === 2 && isStreamingToSidebar && workflowSidebarRef?.current) {
                              workflowSidebarRef.current.endScriptStreaming(result.script);
                              isStreamingToSidebar = false;
                            }
                          },
                          
                          onWorkflowComplete: () => {
                            console.log('[Workflow Resume] Workflow completed');
                            // Clear the context after successful completion
                            setWorkflowContextId(null);
                            // Note: onMessageSent not called here since this is a resume, not a new message
                          },
                          
                          onContextCreated: (contextId: string) => {
                            console.log('[Workflow Resume] Context already exists:', contextId);
                          }
                        };
                        
                        // Create orchestrator and resume from context
                        const orchestrator = new WorkflowOrchestrator(callbacks);
                        setWorkflowOrchestrator(orchestrator);
                        
                        // Check if script already exists (Phase 2 already completed)
                        const hasScript = currentScript !== null && currentScript !== undefined;
                        const scriptId = currentScript?.script_id;
                        await orchestrator.resumeFromContext(session.session_id, workflowContextId, hasScript, scriptId);
                        
                      } catch (error) {
                        console.error('Error resuming workflow:', error);
                        alert(`Failed to resume workflow: ${error instanceof Error ? error.message : 'Unknown error'}`);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#ff6600',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {loading 
                      ? (currentScript ? 'Running QA review...' : 'Generating script...') 
                      : (currentScript ? 'Continue with QA review' : 'Continue with script generation')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Resume Workflow Card - show when workflow was interrupted */}
        {interruptedWorkflow && !loading && (
          <div className="message assistant">
            <div className="message-header">
              <span className="message-role">Assertion</span>
            </div>
            <div className="message-bubble-container">
              <ResumeWorkflowCard
                contextId={interruptedWorkflow.contextId}
                interruptedAt={interruptedWorkflow.interruptedAt}
                reason={interruptedWorkflow.reason}
                currentPhase={interruptedWorkflow.currentPhase}
                canResume={interruptedWorkflow.canResume}
                onResume={async () => {
                  console.log('[Chat] Resuming workflow...');
                  setLoading(true);
                  setInterruptedWorkflow(null);
                  
                  try {
                    // Call resume endpoint
                    const response = await fetch(`/api/workflow/resume`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        context_id: interruptedWorkflow.contextId,
                        resume_token: interruptedWorkflow.resumeToken
                      })
                    });
                    
                    if (response.ok) {
                      // Resume the workflow orchestrator
                      if (workflowOrchestrator && workflowSidebarRef?.current) {
                        await workflowOrchestrator.resumeFromContext(
                          session.session_id,
                          interruptedWorkflow.contextId,
                          currentScript !== null,
                          currentScript?.script_id
                        );
                      }
                    }
                  } catch (error) {
                    console.error('[Chat] Failed to resume workflow:', error);
                    setLoading(false);
                  }
                }}
                onRestart={() => {
                  console.log('[Chat] Restarting workflow...');
                  setInterruptedWorkflow(null);
                  // Clear context and restart
                  setWorkflowContextId(null);
                  setWorkflowOrchestrator(null);
                  // Re-send the original message to start fresh
                  if (messages.length > 0) {
                    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
                    if (lastUserMessage) {
                      setInputMessage(lastUserMessage.content);
                    }
                  }
                }}
                onCancel={() => {
                  console.log('[Chat] Cancelling resume...');
                  setInterruptedWorkflow(null);
                }}
              />
            </div>
          </div>
        )}
        
        {/* Task Identification Card - show after Phase 1 completes with task ID */}
        {taskIdentification && currentMode === 'workflow' && (
          <div className="message assistant">
            <div className="message-header">
              <span className="message-role">Assertion</span>
            </div>
            <div className="message-bubble-container">
              <TaskIdentificationCard
                taskIdentification={taskIdentification}
                onOverride={async (domain, task) => {
                  console.log('[Chat] User overriding task to:', domain, task);
                  
                  // Send override to backend
                  if (workflowContextId) {
                    try {
                      const response = await fetch(`/api/workflow/override-task`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          context_id: workflowContextId,
                          domain,
                          task
                        })
                      });
                      
                      if (response.ok) {
                        // Update local state
                        setTaskIdentification({
                          ...taskIdentification,
                          domain,
                          task,
                          method: 'manual',
                          overridden: true,
                          overriddenAt: new Date().toISOString()
                        });
                        
                        // Show success message
                        const successMessage: Message = {
                          role: 'assistant',
                          content: `✅ Task updated to: **${domain}** → **${task.replace(/_/g, ' ')}**\n\nI'll use the ${task.replace(/_/g, ' ')} framework for this workflow.`,
                          timestamp: new Date().toISOString(),
                        };
                        setMessages(prev => [...prev, successMessage]);
                      }
                    } catch (error) {
                      console.error('[Chat] Failed to override task:', error);
                      const errorMessage: Message = {
                        role: 'assistant',
                        content: '❌ Failed to update task. Please try again.',
                        timestamp: new Date().toISOString(),
                      };
                      setMessages(prev => [...prev, errorMessage]);
                    }
                  }
                }}
              />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
        </div>
        
        {/* Validation Warning */}
        {showValidationWarning && validationIssues.length > 0 && (
          <ValidationWarning
            issues={validationIssues}
            onFix={handleFixWithAI}
            onContinue={handleContinueAnyway}
            isFixing={isFixingIssues}
          />
        )}
        
        {/* File Relationship Review */}
        {showFileRelationshipReview && fileRelationshipData && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowFileRelationshipReview(false);
              if (fileRelationshipResolveRef.current) {
                fileRelationshipResolveRef.current(null);
                fileRelationshipResolveRef.current = null;
              }
            }
          }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '90%' }}>
              <FileRelationshipModal
                relationships={fileRelationshipData.relationships || []}
                filesAnalyzed={fileRelationshipData.files_analyzed || uploadedFiles.map(f => f.filename)}
                recommendations={fileRelationshipData.recommendations}
                confidenceScore={fileRelationshipData.confidence_score}
                contextId={workflowContextId || ''}
                sessionId={session?.session_id || ''}
                onComplete={(relationships) => {
                  console.log('[FileRelationship] User confirmed relationships:', relationships);
                  setShowFileRelationshipReview(false);
                  if (fileRelationshipResolveRef.current) {
                    fileRelationshipResolveRef.current(relationships);
                    fileRelationshipResolveRef.current = null;
                  }
                }}
                onSkip={() => {
                  console.log('[FileRelationship] User skipped relationship review');
                  setShowFileRelationshipReview(false);
                  if (fileRelationshipResolveRef.current) {
                    fileRelationshipResolveRef.current(null);
                    fileRelationshipResolveRef.current = null;
                  }
                }}
              />
            </div>
          </div>
        )}
        
        {/* Save Data Handling Modal */}
        <SaveDataHandlingModal
          isOpen={showSaveDataHandlingModal}
          taskType={taskType}
          dataHandlingChoices={dataHandlingChoices || {}}
          onSave={async () => {
            try {
              setIsSavingPreferences(true);  // Set loading state
              const response = await fetch(`/api/workflow/context/${workflowContextId}/save-data-handling`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  data_handling_choices: dataHandlingChoices,
                  user_accepted: true
                })
              });
              
              if (response.ok) {
                const data = await response.json();
                console.log('[DataHandling] Saved successfully:', data);
                return data.learning_summary || null;
              }
              throw new Error('Failed to save preferences');
            } catch (error) {
              console.error('[DataHandling] Failed to save:', error);
              throw error;
            } finally {
              setIsSavingPreferences(false);  // Clear loading state
            }
          }}
          onSkip={() => {
            console.log('[DataHandling] User skipped saving');
            setShowSaveDataHandlingModal(false);
          }}
          onClose={() => {
            setShowSaveDataHandlingModal(false);
          }}
        />
        
        {/* Fix Preview */}
        {showFixPreview && proposedFixes.length > 0 && currentScript && (
          <FixPreview
            fixes={proposedFixes}
            currentCode={currentScript.code}
            onApply={handleApplyFixes}
            onCancel={() => {
              setShowFixPreview(false);
              setProposedFixes([]);
            }}
            isApplying={false}
          />
        )}
        
        {/* Task Confirmation Modal */}
        {showTaskConfirmation && taskIdentification && (
          <div className="modal-overlay">
            <div className="modal-content task-confirmation-modal">
              <TaskConfirmation
                taskIdentification={taskIdentification}
                onConfirm={(confirmed, selectedTask) => {
                  if (taskConfirmResolveRef.current) {
                    taskConfirmResolveRef.current(confirmed, selectedTask);
                    taskConfirmResolveRef.current = null;
                  }
                  setShowTaskConfirmation(false);
                }}
                onTaskChange={(newTask) => {
                  // Update local state if needed
                  console.log('Task changed to:', newTask);
                }}
              />
            </div>
          </div>
        )}
        

        {/* Metadata Editor */}
        {showMetadataEditor && metadataReviewData && workflowContextId && (
          <MetadataEditor
            reviewData={metadataReviewData}
            contextId={workflowContextId}
            sessionId={session.session_id}
            onConfirm={async () => {
              console.log('[Metadata] User confirmed mappings');
              setShowMetadataEditor(false);
              // Resolve the promise to continue workflow
              if (metadataResolveRef.current) {
                metadataResolveRef.current(true);
                metadataResolveRef.current = null;
              }
            }}
            onCancel={() => {
              console.log('[Metadata] User cancelled review');
              setShowMetadataEditor(false);
              // Resolve the promise to stop workflow
              if (metadataResolveRef.current) {
                metadataResolveRef.current(false);
                metadataResolveRef.current = null;
              }
            }}
          />
        )}
        
        <div className="input-section">
          {/* Show file display for non-workflow modes when files are attached */}
          {currentMode !== 'workflow' && localFiles.length > 0 && (
            <FileDisplay 
              files={localFiles} 
              onRemoveFile={handleRemoveFile}
            />
          )}
          <div 
        className={`input-container chat-mode-${currentMode} ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="mode-selector-wrapper">
          <ModeSelector currentMode={currentMode} onModeChange={handleModeChange} />
        </div>
        {isDragging && (
          <div className="drag-overlay">
            <div className="drag-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <p>Drop files here to upload</p>
            </div>
          </div>
        )}
        {isUploading && (
          <div className="upload-progress">
            <div className="upload-spinner"></div>
            <p>Uploading files...</p>
          </div>
        )}
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={
            currentMode === 'chat' 
              ? localFiles.length > 0
                ? "Ask questions about these files..."
                : "Ask questions or upload files (PDF, Word, images)..."
              : currentMode === 'workflow'
              ? uploadedFiles.length > 0 
                ? "Describe what you want to do with these files..."
                : "Upload files or describe your data task..."
              : localFiles.length > 0
                ? "What should I research about these documents?"
                : "Research topics or analyze documents..."
          }
          disabled={loading}
          rows={4}
        />
        {/* File upload button for all modes */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.csv,.xls,.xlsx"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
        <button
          className="file-upload-button"
          onClick={() => fileInputRef.current?.click()}
          disabled={loading || isUploading}
          title="Upload files"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button 
          className="send-button" 
          onClick={sendMessage} 
          disabled={loading || !inputMessage.trim()}
          title={loading ? 'Loading...' : !inputMessage.trim() ? 'Type a message first' : 'Send message'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        </div>
        
        {/* Quick actions for workflow mode */}
        {currentMode === 'workflow' && uploadedFiles.length > 0 && uploadedFiles.some(f => !('status' in f) || (f as any).status === 'ready') && (
          <div className="quick-actions">
            <button className="btn btn-ghost btn-sm" onClick={() => sendQuickPrompt("Generate a script to analyze this data")}>
              Quick Analysis
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => sendQuickPrompt("Generate a script to clean and validate this data")}>
              Data Cleaning
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => sendQuickPrompt("Generate a script to create a summary report")}>
              Summary Report
            </button>
          </div>
        )}
        
        {/* Quick actions for chat mode with files */}
        {currentMode === 'chat' && localFiles.length > 0 && (
          <div className="quick-actions">
            <button className="btn btn-ghost btn-sm" onClick={() => sendQuickPrompt("Summarize these documents")}>
              Summarize
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => sendQuickPrompt("Extract key information from these files")}>
              Extract Info
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => sendQuickPrompt("What are the main points in these documents?")}>
              Key Points
            </button>
          </div>
        )}
        
        {/* Quick actions for research mode with files */}
        {currentMode === 'research' && localFiles.length > 0 && (
          <div className="quick-actions">
            <button className="btn btn-ghost btn-sm" onClick={() => sendQuickPrompt("Research the topics mentioned in these documents")}>
              Research Topics
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => sendQuickPrompt("Fact-check the claims in these files")}>
              Fact Check
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => sendQuickPrompt("Find related regulations or standards")}>
              Find Regulations
            </button>
          </div>
        )}
        </div>
        
        {/* Interrupt Modal */}
        <InterruptModal
          isOpen={showInterruptModal}
          escPressCount={escPressCount}
          onConfirm={handleInterrupt}
          onCancel={cancelInterrupt}
        />
      </div>
    </div>
  );
};

export default Chat;